let logoId = 0;

/** The same six-second artwork is used on the website and in the video export. */
export function createAnimatedLogo(container, { source, controls = true, autoplay = true, theme = 'light' } = {}) {
  const id = `studio-motion-${++logoId}`;
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.createElement('div');
  root.className = 'logo-motion';
  root.dataset.state = 'static';
  root.dataset.theme = theme === 'dark' ? 'dark' : 'light';
  root.innerHTML = `
    <img class="logo-motion__fallback" alt="Sefidel Game Studio" width="1254" height="1254" />
    <svg class="logo-motion__art" viewBox="0 0 1254 1254" aria-hidden="true" focusable="false">
      <defs>
        <clipPath id="${id}-emblem"><rect width="1254" height="795" /></clipPath>
        <clipPath id="${id}-name"><rect y="795" width="1254" height="255" /></clipPath>
        <clipPath id="${id}-tagline"><rect y="1050" width="1254" height="204" /></clipPath>
        <filter id="${id}-glow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <linearGradient id="${id}-shine" x1="0" x2="1">
          <stop offset="0" stop-color="white" stop-opacity="0" />
          <stop offset=".5" stop-color="white" stop-opacity=".65" />
          <stop offset="1" stop-color="white" stop-opacity="0" />
        </linearGradient>
      </defs>
      <g class="logo-motion__emblem">
        <g clip-path="url(#${id}-emblem)"><image width="1254" height="1254" /></g>
      </g>
      <g class="logo-motion__name">
        <g clip-path="url(#${id}-name)"><image width="1254" height="1254" /></g>
      </g>
      <g class="logo-motion__tagline">
        <g clip-path="url(#${id}-tagline)"><image width="1254" height="1254" /></g>
      </g>
      <g class="logo-motion__orbit" fill="none" stroke-linecap="round">
        <path class="logo-motion__trail" pathLength="1" d="M 440 613 C 579 616 1017 393 929 342 C 884 309 781 340 708 369"
          stroke="#00d8f7" stroke-width="15" filter="url(#${id}-glow)" />
        <path class="logo-motion__trail" pathLength="1" d="M 440 613 C 579 616 1017 393 929 342 C 884 309 781 340 708 369"
          stroke="#72f3ff" stroke-width="4" />
      </g>
      <g class="logo-motion__spark" transform="translate(603 295)">
        <path d="M 0 -43 Q 5 -5 43 0 Q 5 5 0 43 Q -5 5 -43 0 Q -5 -5 0 -43" fill="#60eaff" filter="url(#${id}-glow)" />
        <path d="M 0 -28 Q 4 -4 28 0 Q 4 4 0 28 Q -4 4 -28 0 Q -4 -4 0 -28" fill="white" />
      </g>
      <rect class="logo-motion__shine" x="-400" y="100" width="220" height="1030" fill="url(#${id}-shine)" transform="skewX(-18)" />
    </svg>`;
  root.querySelector('img').src = source;
  root.querySelectorAll('image').forEach(image => image.setAttribute('href', source));

  let button;
  const setState = state => {
    root.dataset.state = state;
    if (button) {
      const playing = state === 'playing' || state === 'settling';
      button.textContent = playing ? 'Stop animation' : 'Replay logo';
      button.setAttribute('aria-label', playing ? 'Stop logo animation' : 'Replay logo animation');
    }
  };
  const play = () => {
    if (motionPreference.matches) return;
    setState('static');
    // Reset the CSS timeline so replay always starts at the beginning.
    void root.offsetWidth;
    setState('playing');
  };
  if (controls) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'logo-motion__control';
    button.hidden = true;
    button.addEventListener('click', () => {
      if (root.dataset.state === 'static') play();
      else setState('static');
    });
    root.append(button);
    setState('static');
  }

  // The exported clip ends on its background. The website returns to the complete
  // logo after the exit, keeping the studio name present while visitors browse.
  root.addEventListener('animationend', event => {
    if (event.animationName === 'studio-emblem' && root.dataset.state === 'playing') {
      setState('settling');
    } else if (event.animationName === 'studio-settle') {
      setState('static');
    }
  });
  motionPreference.addEventListener('change', () => {
    setState('static');
    if (button) button.hidden = motionPreference.matches || !root.classList.contains('is-ready');
  });
  document.addEventListener('visibilitychange', () => {
    root.classList.toggle('is-suspended', document.hidden);
  });
  container.replaceChildren(root);
  const ready = root.querySelector('img').decode().then(() => {
    root.classList.add('is-ready');
    if (button) button.hidden = motionPreference.matches;
    if (autoplay) play();
  }).catch(() => {
    // Leave the ordinary image in place if decoding/animation is unavailable.
    setState('static');
  });
  return { root, ready, play };
}
